<template>
	<b-modal
		:visible="visible"
		hide-header
		hide-footer
		centered
		size="xl"
		no-close-on-backdrop
		content-class="advertisement-modal"
		@change="onVisibilityChange"
	>
		<div class="advertisement-modal-body">
			<button type="button" class="advertisement-modal-close" @click="$emit('close')">
				<img src="@/assets/images/cancel-icon.svg" alt="Close" />
			</button>

			<!-- Step rail -->
			<div class="advertisement-steps">
				<div
					v-for="(step, index) in steps"
					:key="step.title"
					class="advertisement-step"
					:class="{ active: currentStep === index + 1, completed: currentStep > index + 1 }"
					@click="goToStep(index + 1)"
				>
					<div class="advertisement-step-circle">{{ index + 1 }}</div>
					<div>
						<h3 class="advertisement-step-title">{{ step.title }}</h3>
						<p class="advertisement-step-description">{{ step.description }}</p>
					</div>
				</div>
			</div>

			<div class="advertisement-step-main">
				<h1 class="advertisement-modal-title">{{ advertisement ? "Edit" : "Add New" }} Advertisement</h1>

				<div class="advertisement-step-body">
					<!-- Step 1: content -->
					<div v-show="currentStep === 1">
						<div v-if="IS_HTML_ADVERTISEMENT_ENABLED" class="advertisement-form-group">
							<label class="main-label">Type</label>
							<div class="advertisement-option-cards">
								<div v-for="option in typeOptions" :key="option.value" class="advertisement-option-card-container">
									<input :id="`advertisement-type-${option.value}`" v-model="type" type="radio" name="advertisement-type" :value="option.value" />
									<label class="advertisement-option-card" :for="`advertisement-type-${option.value}`">
										<p>{{ option.title }}</p>
										{{ option.description }}
									</label>
								</div>
							</div>
						</div>

						<div v-if="type === AdvertisementType.CARD" class="advertisement-form-group">
							<label class="main-label">Image (required)</label>
							<div v-if="image" class="advertisement-image-preview">
								<img :src="image" alt="" />
								<button type="button" class="advertisement-image-remove" title="Remove image" @click="removeImage">
									<img src="@/assets/images/cancel-icon.svg" alt="Remove" />
								</button>
							</div>
							<vue-dropzone
								v-else
								id="advertisement-dropzone"
								class="advertisement-dropzone"
								:options="dropzoneOptions"
								:useCustomSlot="true"
								@vdropzone-file-added="uploadImage"
							>
								<p class="advertisement-dropzone-title">{{ isUploading ? "Uploading..." : "Drag and drop an image" }}</p>
								<p class="advertisement-dropzone-subtitle">or click to select a file.</p>
							</vue-dropzone>
							<input v-if="!isImageUploaded" v-model.trim="image" class="form-input mt-2" placeholder="Or paste an image URL" />
						</div>

						<div v-else class="advertisement-form-group">
							<label class="main-label">HTML (required)</label>
							<textarea v-model="html" rows="10" class="form-textarea" placeholder="<div>...</div>"></textarea>
							<small class="text-muted">Do not put links inside. Set the link in the next step and the whole card becomes clickable.</small>
						</div>

						<div class="advertisement-form-group">
							<label class="main-label">{{ type === AdvertisementType.HTML ? "Name" : "Title" }}</label>
							<input v-model.trim="title" class="form-input" :placeholder="type === AdvertisementType.HTML ? 'Type a name to find it in the list' : 'Type the title'" />
							<small v-if="type === AdvertisementType.HTML" class="text-muted">Only admins see it.</small>
						</div>
						<div v-if="type === AdvertisementType.CARD" class="advertisement-form-group">
							<label class="main-label">Subtitle</label>
							<input v-model.trim="subtitle" class="form-input" placeholder="Type the subtitle" />
						</div>
						<div v-if="type === AdvertisementType.CARD" class="form-check form-switch advertisement-form-group">
							<input id="advertisement-overlay" v-model="isOverlay" class="form-check-input" type="checkbox" role="switch" />
							<label class="form-check-label" for="advertisement-overlay">Show text over the image</label>
						</div>
					</div>

					<!-- Step 2: link and audience -->
					<div v-show="currentStep === 2">
						<div class="advertisement-form-group">
							<label class="main-label">Link</label>
							<select v-model="linkType" class="form-input">
								<option :value="LinkType.NONE">No link</option>
								<option :value="LinkType.COURSE">A course</option>
								<option :value="LinkType.CUSTOM">Custom link</option>
							</select>
							<v-select
								v-if="linkType === LinkType.COURSE"
								v-model="linkedCourseId"
								class="form-input mt-2"
								:options="publishedCourseOptions"
								:reduce="(option) => option.id"
								label="label"
								placeholder="Search by ID or title"
							/>
							<input v-if="linkType === LinkType.CUSTOM" v-model.trim="customLink" class="form-input mt-2" placeholder="/search or https://..." />
						</div>

						<div v-if="type === AdvertisementType.CARD && linkType !== LinkType.NONE" class="advertisement-form-group">
							<label class="main-label">Button text</label>
							<input v-model.trim="buttonText" class="form-input" placeholder="Type the button text" />
							<small class="text-muted">Leave empty to make the whole card clickable instead.</small>
						</div>

						<div class="form-check form-switch advertisement-form-group">
							<input id="advertisement-student-dashboard" v-model="showOnStudentDashboard" class="form-check-input" type="checkbox" role="switch" />
							<label class="form-check-label" for="advertisement-student-dashboard">Show on Student Dashboard</label>
						</div>

						<div class="advertisement-form-group">
							<label class="main-label">Show on course pages for</label>
							<v-select
								v-model="productVariantIds"
								class="form-input"
								multiple
								:options="courseOptions"
								:reduce="(option) => option.id"
								:closeOnSelect="false"
								label="label"
								placeholder="Search by ID or title"
							/>
							<small class="text-muted">Leave empty to keep it off course pages.</small>
						</div>

						<div class="form-check form-switch">
							<input id="advertisement-active" v-model="isActive" class="form-check-input" type="checkbox" role="switch" />
							<label class="form-check-label" for="advertisement-active">Active</label>
						</div>
					</div>

					<!-- Step 3: preview -->
					<div v-if="currentStep === 3">
						<div class="advertisement-form-group">
							<label class="main-label">Preview mode</label>
							<div class="advertisement-option-cards">
								<div v-for="mode in previewModes" :key="mode.value" class="advertisement-option-card-container">
									<input :id="`advertisement-preview-${mode.value}`" v-model="previewMode" type="radio" name="advertisement-preview" :value="mode.value" />
									<label class="advertisement-option-card" :for="`advertisement-preview-${mode.value}`">
										<p>{{ mode.title }}</p>
										{{ mode.description }}
									</label>
								</div>
							</div>
						</div>
						<div v-if="previewMode === PreviewMode.COMPACT" class="alert alert-danger py-2 small">
							Compact mode is used where space is narrow, such as the course sidebar. Its card height follows the Sidebar card height setting.
						</div>
						<div class="advertisement-preview-frame" :class="{ compact: previewMode === PreviewMode.COMPACT }">
							<AdvertisementBanner
								:advertisement="draftSlide"
								:compact="previewMode === PreviewMode.COMPACT"
								:height="previewMode === PreviewMode.COMPACT ? settings.compactHeight : settings.height"
								preview
							/>
						</div>
					</div>

					<div v-if="showErrors" class="mt-3">
						<p v-for="error in currentStepErrors" :key="error" class="text-danger small mb-1">{{ error }}</p>
					</div>
				</div>

				<!-- Navigation -->
				<div class="advertisement-step-navigation">
					<button type="button" class="advertisement-step-button secondary" @click="$emit('close')">Cancel</button>
					<button v-if="currentStep > 1" type="button" class="advertisement-step-button secondary" @click="goToStep(currentStep - 1)">Previous</button>
					<button v-if="currentStep < steps.length" type="button" class="advertisement-step-button" @click="nextStep">
						Next
						<img src="@/assets/images/arrow-right.svg" alt="" />
					</button>
					<button v-else type="button" class="advertisement-step-button" :disabled="isUploading" @click="$emit('save', draftSlide)">Done</button>
				</div>
			</div>
		</div>
	</b-modal>
</template>

<script>
import vue2Dropzone from "vue2-dropzone";
import { mapActions } from "vuex";
import AdvertisementBanner from "../../../Advertisement/AdvertisementBanner.vue";
import { ProductVariantStatus } from "../../../../constants/productVariant";
import { AdvertisementType, IS_HTML_ADVERTISEMENT_ENABLED, LinkType, PreviewMode } from "../../../../constants/advertisement";

const COURSE_LINK_PREFIX = "/product/details/";

export default {
	name: "AddEditAdvertisementModal",
	components: { vueDropzone: vue2Dropzone, AdvertisementBanner },
	props: {
		visible: { type: Boolean, default: false },
		advertisement: { type: Object, default: null },
		settings: { type: Object, default: () => ({}) },
		courseOptions: { type: Array, default: () => [] },
	},
	data() {
		return {
			steps: [
				{ title: "Content", description: IS_HTML_ADVERTISEMENT_ENABLED ? "Image or HTML, title and subtitle" : "Image, title and subtitle" },
				{ title: "Link and audience", description: "Where it goes and who sees it" },
				{ title: "Preview and save", description: "Check both placements" },
			],
			typeOptions: [
				{ value: AdvertisementType.CARD, title: "Card", description: "Image, title, subtitle and an optional button." },
				{ value: AdvertisementType.HTML, title: "HTML", description: "Your own markup: badges, panels, embeds." },
			],
			previewModes: [
				{ value: PreviewMode.STANDARD, title: "Standard", description: "Full size, as on the Student Dashboard and course pages." },
				{ value: PreviewMode.COMPACT, title: "Compact", description: "Narrow, as in the ebook course sidebar." },
			],
			previewMode: PreviewMode.STANDARD,
			currentStep: 1,
			showErrors: false,
			AdvertisementType,
			IS_HTML_ADVERTISEMENT_ENABLED,
			LinkType,
			PreviewMode,
			type: AdvertisementType.CARD,
			image: "",
			// An uploaded image hides the URL field
			isImageUploaded: false,
			title: "",
			subtitle: "",
			isOverlay: false,
			buttonText: "",
			html: "",
			linkType: LinkType.NONE,
			linkedCourseId: null,
			customLink: "",
			productVariantIds: [],
			isActive: true,
			showOnStudentDashboard: true,
			isUploading: false,
			dropzoneOptions: {
				url: " ",
				autoProcessQueue: false,
				maxFiles: 1,
				acceptedFiles: "image/*",
				previewTemplate: "<div class='d-none'></div>",
			},
		};
	},
	computed: {
		publishedCourseOptions() {
			return this.courseOptions.filter((option) => option.status === ProductVariantStatus.PUBLISHED.toLowerCase());
		},
		link() {
			if (this.linkType === LinkType.COURSE) {
				return this.linkedCourseId ? `${COURSE_LINK_PREFIX}${this.linkedCourseId}` : "";
			}

			return this.linkType === LinkType.CUSTOM ? this.customLink : "";
		},
		draftSlide() {
			const slide = { isActive: this.isActive, showOnStudentDashboard: this.showOnStudentDashboard };
			if (this.title) {
				slide.title = this.title;
			}
			if (this.type === AdvertisementType.HTML) {
				slide.html = this.html;
			} else {
				slide.image = this.image;
				if (this.isOverlay) {
					slide.overlay = true;
				}
				if (this.subtitle) {
					slide.subtitle = this.subtitle;
				}
				if (this.buttonText && this.link) {
					slide.buttonText = this.buttonText;
				}
			}
			if (this.link) {
				slide.link = this.link;
			}
			if (this.productVariantIds.length > 0) {
				slide.productVariantIds = this.productVariantIds;
			}

			return slide;
		},
		currentStepErrors() {
			const errors = [];
			if (this.currentStep === 1) {
				if (this.type === AdvertisementType.CARD && !this.image) {
					errors.push("Add an image.");
				}
				if (this.type === AdvertisementType.HTML && !this.html.trim()) {
					errors.push("Add the HTML.");
				}
			}
			if (this.currentStep === 2) {
				const customLink = this.customLink.toLowerCase();
				const isCustomLinkValid = customLink.startsWith("/") || customLink.startsWith("http");
				if (this.linkType === LinkType.COURSE && !this.linkedCourseId) {
					errors.push("Pick a course to link to.");
				}
				if (this.linkType === LinkType.CUSTOM && !isCustomLinkValid) {
					errors.push("A custom link must start with / or http.");
				}
				if (!this.showOnStudentDashboard && this.productVariantIds.length === 0) {
					errors.push("Show it on the Student Dashboard or pick at least one batch.");
				}
			}

			return errors;
		},
	},
	watch: {
		visible(isVisible) {
			if (isVisible) {
				this.loadAdvertisement(this.advertisement || {});
			}
		},
	},
	methods: {
		...mapActions("content", ["uploadContentFile"]),
		loadAdvertisement(slide) {
			const link = String(slide.link || "");
			const isCourseLink = link.toLowerCase().startsWith(COURSE_LINK_PREFIX);

			this.currentStep = 1;
			this.showErrors = false;
			this.previewMode = PreviewMode.STANDARD;
			this.type = slide.html ? AdvertisementType.HTML : AdvertisementType.CARD;
			this.image = slide.image || "";
			this.isImageUploaded = false;
			this.title = slide.title || "";
			this.subtitle = slide.subtitle || "";
			this.isOverlay = String(slide.overlay).toLowerCase() === "true";
			this.buttonText = slide.buttonText || "";
			this.html = slide.html || "";
			this.linkType = link ? (isCourseLink ? LinkType.COURSE : LinkType.CUSTOM) : LinkType.NONE;
			this.linkedCourseId = isCourseLink ? link.slice(COURSE_LINK_PREFIX.length) : null;
			this.customLink = isCourseLink ? "" : link;
			this.productVariantIds = [].concat(slide.productVariantIds || []).map(String);
			this.isActive = slide.isActive !== false;
			this.showOnStudentDashboard = slide.showOnStudentDashboard !== false;
		},
		goToStep(targetStep) {
			if (targetStep < this.currentStep) {
				this.currentStep = targetStep;
				this.showErrors = false;
			}
		},
		nextStep() {
			this.showErrors = this.currentStepErrors.length > 0;
			if (!this.showErrors) {
				this.currentStep += 1;
			}
		},
		removeImage() {
			this.image = "";
			this.isImageUploaded = false;
		},
		uploadImage(file) {
			const formData = new FormData();
			formData.append("file", file);
			formData.append("entity", "Banner");
			formData.append("IsCompressionRequired", true);
			formData.append("type", "Image");

			this.isUploading = true;
			this.uploadContentFile(formData).then((url) => {
				this.image = url || "";
				this.isImageUploaded = Boolean(url);
				this.isUploading = false;
			});
		},
		onVisibilityChange(isVisible) {
			if (!isVisible) {
				this.$emit("close");
			}
		},
	},
};
</script>

<style>
.advertisement-modal .modal-body {
	display: flex;
	padding: 0 !important;
	overflow: hidden;
}
</style>

<style scoped>
/* Layout */
.advertisement-modal-body {
	position: relative;
	display: flex;
	width: 100%;
	min-height: 40rem;
}
.advertisement-modal-close {
	position: absolute;
	top: 2.5rem;
	right: 2rem;
	border: 0;
	background: transparent;
}
.advertisement-modal-close img {
	height: 1.5rem;
}

/* Step rail */
.advertisement-steps {
	display: flex;
	flex-direction: column;
	justify-content: center;
	flex-shrink: 0;
	width: 300px;
	padding: 2.5rem 0;
	background-color: #f5f5f5;
}
.advertisement-step {
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	margin: 1rem 0;
	padding: 1rem 0 1rem 2rem;
	border-left: 2px solid #e5e7eb;
}
.advertisement-step.completed {
	border-left-color: var(--primary-color);
	cursor: pointer;
}
.advertisement-step-circle {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #e5e7eb;
	color: #6b7280;
	font-size: 14px;
	font-weight: bold;
}
.advertisement-step.active .advertisement-step-circle,
.advertisement-step.completed .advertisement-step-circle {
	background-color: var(--primary-color);
	color: #ffffff;
}
.advertisement-step-title {
	margin: 0 0 0.25rem;
	font-size: 16px;
	font-weight: 600;
	color: #374151;
}
.advertisement-step.active .advertisement-step-title {
	color: var(--primary-color);
}
.advertisement-step-description {
	margin: 0;
	font-size: 14px;
	color: #6b7280;
}

/* Main area */
.advertisement-step-main {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
}
.advertisement-modal-title {
	margin: 3.5rem 2rem 2rem;
	font-size: 2rem;
	font-weight: 450;
	line-height: 1.5rem;
	color: #000000;
}
.advertisement-step-body {
	flex: 1;
	height: 60vh;
	padding: 0 2rem;
	overflow: auto;
}
.advertisement-step-body .main-label {
	display: block;
	margin-bottom: 1rem;
	font-weight: 500;
	font-size: 1rem;
	line-height: 1.37;
	text-transform: uppercase;
	color: #727272;
}
.advertisement-step-body .form-input {
	width: 100%;
	padding: 0.5rem 0;
	border: 0;
	border-bottom: 1.5px solid #b2b2b2;
	border-radius: 0;
	outline: none;
	box-shadow: none;
	background-color: transparent;
}
.advertisement-step-body .form-textarea {
	width: 100%;
	padding: 0.5rem;
	border: 1.5px solid #b2b2b2;
	border-radius: 6px;
	font-family: monospace;
	font-size: 0.8125rem;
}
.advertisement-form-group {
	margin-bottom: 1.5rem;
}

/* Option cards */
.advertisement-option-cards {
	display: flex;
	flex-wrap: wrap;
	gap: 1.5rem;
}
.advertisement-option-card-container input[type="radio"] {
	position: fixed;
	width: 0;
	opacity: 0;
}
.advertisement-option-card {
	position: relative;
	display: block;
	max-width: 13.75rem;
	height: 100%;
	padding: 1.125rem;
	border: 1px solid #dcdcdc;
	border-radius: 6px;
	box-shadow: 2px 3px 5px rgba(197, 197, 197, 0.15);
	background-color: #ffffff;
	color: #727272;
	font-size: 1rem;
	line-height: 1.37;
	cursor: pointer;
}
.advertisement-option-card p {
	margin-bottom: 0.5rem;
	color: #000000;
	font-weight: 500;
}
.advertisement-option-card-container input[type="radio"]:checked + .advertisement-option-card {
	border-color: var(--primary-color);
}
.advertisement-option-card-container input[type="radio"]:checked + .advertisement-option-card::after {
	content: "";
	position: absolute;
	top: -0.5rem;
	right: -0.5rem;
	width: 1.625rem;
	height: 1.625rem;
	border-radius: 50%;
	background: var(--primary-color) url("~@/assets/images/check-icon.svg") no-repeat center / 0.8rem auto;
}

/* Image */
.advertisement-dropzone {
	padding: 2rem;
	border: 2px dashed #dcdcdc;
	border-radius: 6px;
	background-color: #fafafa;
	text-align: center;
	cursor: pointer;
}
.advertisement-dropzone-title {
	margin: 0;
	font-weight: 500;
	color: #231f20;
}
.advertisement-dropzone-subtitle {
	margin: 0.25rem 0 0;
	font-size: 0.8125rem;
	color: #838383;
}
.advertisement-image-preview {
	position: relative;
	width: fit-content;
}
.advertisement-image-preview img {
	width: 16rem;
	height: 9rem;
	object-fit: cover;
	border: 1px solid #dcdcdc;
	border-radius: 6px;
}
.advertisement-image-remove {
	position: absolute;
	top: 0.5rem;
	right: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	padding: 0;
	background: #ffffff;
	border: 0.8px solid #ff0000;
	box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.07);
	border-radius: 50%;
}
.advertisement-image-preview .advertisement-image-remove img {
	width: 0.75rem;
	height: 0.75rem;
	border: 0;
}

/* Preview */
.advertisement-preview-frame.compact {
	width: 235px;
}

/* Navigation */
.advertisement-step-navigation {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 1rem;
	padding: 2rem;
}
.advertisement-step-button {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	column-gap: 0.5rem;
	padding: 0.875rem;
	border: 1px solid var(--primary-color);
	border-radius: 6px;
	box-sizing: border-box;
	background: #ffffff;
	filter: drop-shadow(3px 5px 6px rgba(197, 197, 197, 0.3));
	color: #000000;
	font-size: 0.875rem;
	line-height: 1;
	text-transform: uppercase;
	text-decoration: none;
}
.advertisement-step-button.secondary {
	border: 1px solid #e5e7eb;
	color: #374151;
}
.advertisement-step-button:hover:not(:disabled) {
	border-color: #ffffff;
	background-color: var(--primary-color);
	color: #ffffff;
}
.advertisement-step-button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
.advertisement-step-button img {
	height: 0.75rem;
}
@media (max-width: 992px) {
	.advertisement-steps {
		display: none;
	}
}
</style>
